/**
 * Shared scroll animations for Oura-style sections.
 * Uses IntersectionObserver for fade-in and scroll-linked transforms for parallax.
 *
 * Data attributes:
 *   data-coa-fade          — fade-in + blur + slide-up on enter
 *   data-coa-parallax="N"  — scroll-linked vertical parallax (speed multiplier)
 *   data-coa-rotate="N"    — scroll-linked rotation (start degrees)
 *   data-coa-rotate-speed  — rotation speed multiplier
 *   data-coa-scale="N"     — scroll-linked scale (start scale, grows to 1.0 at center)
 *   data-coa-fade-scroll   — scroll-linked opacity (fade in/out based on viewport position)
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Fade-in + blur + slide-up (IntersectionObserver) ── */

  function initFadeAnimations() {
    var elements = document.querySelectorAll('[data-coa-fade]');
    if (!elements.length) return;

    if (reducedMotion) {
      elements.forEach(function (el) {
        el.style.opacity = '1';
        el.style.filter = 'none';
        el.style.transform = 'none';
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('coa-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach(function (el) { observer.observe(el); });
  }

  /* ── Scroll-linked parallax + rotation + scale + fade ── */

  var parallaxElements = [];
  var ticking = false;

  function findSection(el) {
    var node = el;
    while (node && node !== document.body) {
      if (node.tagName === 'SECTION' || node.hasAttribute('data-section-type')) return node;
      node = node.parentElement;
    }
    return el.parentElement;
  }

  function initParallax() {
    var els = document.querySelectorAll('[data-coa-parallax], [data-coa-rotate], [data-coa-scale], [data-coa-fade-scroll]');
    if (!els.length || reducedMotion) return;

    els.forEach(function (el) {
      parallaxElements.push({
        el: el,
        section: findSection(el),
        speed: parseFloat(el.getAttribute('data-coa-parallax')) || 0,
        startRotate: parseFloat(el.getAttribute('data-coa-rotate')) || 0,
        rotateSpeed: parseFloat(el.getAttribute('data-coa-rotate-speed')) || 0.05,
        startScale: parseFloat(el.getAttribute('data-coa-scale')) || 0,
        fadeScroll: el.hasAttribute('data-coa-fade-scroll')
      });
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    updateParallax();
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(updateParallax);
      ticking = true;
    }
  }

  /**
   * Easing helper: creates a bell curve (0 → 1 → 0) centered at progress 0.5
   * Used for scale and opacity so elements peak when centered in viewport.
   */
  function bellCurve(progress) {
    return 1 - Math.pow(2 * progress - 1, 2);
  }

  function updateParallax() {
    var scrollY = window.pageYOffset;
    var viewH = window.innerHeight;

    parallaxElements.forEach(function (item) {
      var rect = item.section.getBoundingClientRect();

      /* Only animate when section is near viewport */
      if (rect.bottom < -viewH || rect.top > viewH * 2) return;

      var sectionTop = rect.top + scrollY;
      var progress = (scrollY - sectionTop + viewH) / (rect.height + viewH);
      progress = Math.max(0, Math.min(1, progress));

      var transforms = [];

      /* Parallax translateY */
      if (item.speed) {
        var translateY = (progress - 0.5) * item.speed * 200;
        transforms.push('translateY(' + translateY + 'px)');
      }

      /* Rotation */
      if (item.startRotate) {
        var rotation = item.startRotate + (progress - 0.5) * item.rotateSpeed * 360;
        transforms.push('rotate(' + rotation + 'deg)');
      }

      /* Scale: grows from startScale to 1.0 at center, then shrinks back */
      if (item.startScale) {
        var scaleRange = 1 - item.startScale;
        var scale = item.startScale + scaleRange * bellCurve(progress);
        transforms.push('scale(' + scale.toFixed(3) + ')');
      }

      /* Apply transforms */
      if (transforms.length) {
        item.el.style.transform = transforms.join(' ');
      }

      /* Scroll-linked opacity fade */
      if (item.fadeScroll) {
        /* Fade in from 0→1 in first 30%, full opacity in middle, fade out 1→0 in last 30% */
        var opacity;
        if (progress < 0.2) {
          opacity = progress / 0.2;
        } else if (progress > 0.85) {
          opacity = (1 - progress) / 0.15;
        } else {
          opacity = 1;
        }
        item.el.style.opacity = Math.max(0, Math.min(1, opacity)).toFixed(2);
      }
    });

    ticking = false;
  }

  /* ── Init ── */

  function init() {
    initFadeAnimations();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
